import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { Months } from './months.js';

function isDateValid(date) {
  return date instanceof Date && !isNaN(date.valueOf());
}
function areRangesEqual(dateRange1, dateRange2) {
  if (dateRange1 == null && dateRange2 == null) {
    return true;
  } else if (dateRange1 == null || dateRange2 == null) {
    return false;
  } else {
    var _dateRange = _slicedToArray(dateRange1, 2),
        start1 = _dateRange[0],
        end1 = _dateRange[1];

    var _dateRange2 = _slicedToArray(dateRange2, 2),
        start2 = _dateRange2[0],
        end2 = _dateRange2[1];

    var areStartsEqual = start1 == null && start2 == null || areSameDay(start1, start2);
    var areEndsEqual = end1 == null && end2 == null || areSameDay(end1, end2);
    return areStartsEqual && areEndsEqual;
  }
}
function areSameDay(date1, date2) {
  return areSameMonth(date1, date2) && date1.getDate() === date2.getDate();
}
function areSameMonth(date1, date2) {
  return date1 != null && date2 != null && date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
}
function areSameTime(date1, date2) {
  return date1 != null && date2 != null && date1.getHours() === date2.getHours() && date1.getMinutes() === date2.getMinutes() && date1.getSeconds() === date2.getSeconds() && date1.getMilliseconds() === date2.getMilliseconds();
}
function clone(d) {
  return new Date(d.getTime());
}
function isDayInRange(date, dateRange) {
  var exclusive = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  if (date == null) {
    return false;
  }

  var day = clone(date);
  var start = clone(dateRange[0]);
  var end = clone(dateRange[1]);
  day.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return start <= day && day <= end && (!exclusive || !areSameDay(start, day) && !areSameDay(day, end));
}
function isDayRangeInRange(innerRange, outerRange) {
  return (innerRange[0] == null || isDayInRange(innerRange[0], outerRange)) && (innerRange[1] == null || isDayInRange(innerRange[1], outerRange));
}
function isMonthInRange(date, dateRange) {
  if (date == null) {
    return false;
  }

  var day = clone(date);
  var start = clone(dateRange[0]);
  var end = clone(dateRange[1]);
  day.setDate(1);
  start.setDate(1);
  end.setDate(1);
  day.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return start <= day && day <= end;
}
var isTimeEqualOrGreaterThan = function isTimeEqualOrGreaterThan(time, timeToCompare) {
  return time.getTime() >= timeToCompare.getTime();
};
var isTimeEqualOrSmallerThan = function isTimeEqualOrSmallerThan(time, timeToCompare) {
  return time.getTime() <= timeToCompare.getTime();
};
function isTimeInRange(date, minDate, maxDate) {
  var time = getDateOnlyWithTime(date);
  var minTime = getDateOnlyWithTime(minDate);
  var maxTime = getDateOnlyWithTime(maxDate);
  var isTimeGreaterThanMinTime = isTimeEqualOrGreaterThan(time, minTime);
  var isTimeSmallerThanMaxTime = isTimeEqualOrSmallerThan(time, maxTime);

  if (isTimeEqualOrSmallerThan(maxTime, minTime)) {
    return isTimeGreaterThanMinTime || isTimeSmallerThanMaxTime;
  }

  return isTimeGreaterThanMinTime && isTimeSmallerThanMaxTime;
}
function getTimeInRange(time, minTime, maxTime) {
  if (areSameTime(minTime, maxTime)) {
    return maxTime;
  } else if (isTimeInRange(time, minTime, maxTime)) {
    return time;
  } else if (isTimeSameOrAfter(time, maxTime)) {
    return maxTime;
  }

  return minTime;
}
/**
 * Returns true if the time part of `date` is later than or equal to the time
 * part of `dateToCompare`. The day, month, and year parts will not be compared.
 */

function isTimeSameOrAfter(date, dateToCompare) {
  var time = getDateOnlyWithTime(date);
  var timeToCompare = getDateOnlyWithTime(dateToCompare);
  return isTimeEqualOrGreaterThan(time, timeToCompare);
}
/**
 * @returns a Date at the exact time-wise midpoint between startDate and endDate
 */

function getDateBetween(dateRange) {
  var _dateRange$0$getTime, _dateRange$, _dateRange$1$getTime, _dateRange$2;

  var start = (_dateRange$0$getTime = (_dateRange$ = dateRange[0]) === null || _dateRange$ === void 0 ? void 0 : _dateRange$.getTime()) !== null && _dateRange$0$getTime !== void 0 ? _dateRange$0$getTime : 0;
  var end = (_dateRange$1$getTime = (_dateRange$2 = dateRange[1]) === null || _dateRange$2 === void 0 ? void 0 : _dateRange$2.getTime()) !== null && _dateRange$1$getTime !== void 0 ? _dateRange$1$getTime : 0;
  var middle = start + (end - start) * 0.5;
  return new Date(middle);
}
function getDateTime(date, time) {
  if (date == null) {
    return null;
  } else if (time == null) {
    // cannot use default argument because `null` is a common value in this package.
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  } else {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
  }
}
function getDateOnlyWithTime(date) {
  return new Date(0, 0, 0, date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
}
function getDatePreviousMonth(date) {
  if (date.getMonth() === Months.JANUARY) {
    return new Date(date.getFullYear() - 1, Months.DECEMBER);
  } else {
    return new Date(date.getFullYear(), date.getMonth() - 1);
  }
}
function getDateNextMonth(date) {
  if (date.getMonth() === Months.DECEMBER) {
    return new Date(date.getFullYear() + 1, Months.JANUARY);
  } else {
    return new Date(date.getFullYear(), date.getMonth() + 1);
  }
}
function convert24HourMeridiem(hour, toPm) {
  if (hour < 0 || hour > 23) {
    throw new Error("hour must be between [0,23] inclusive: got ".concat(hour));
  }

  return toPm ? hour % 12 + 12 : hour % 12;
}
function getIsPmFrom24Hour(hour) {
  if (hour < 0 || hour > 23) {
    throw new Error("hour must be between [0,23] inclusive: got ".concat(hour));
  }

  return hour >= 12;
}
function get12HourFrom24Hour(hour) {
  if (hour < 0 || hour > 23) {
    throw new Error("hour must be between [0,23] inclusive: got ".concat(hour));
  }

  var newHour = hour % 12;
  return newHour === 0 ? 12 : newHour;
}
function get24HourFrom12Hour(hour, isPm) {
  if (hour < 1 || hour > 12) {
    throw new Error("hour must be between [1,12] inclusive: got ".concat(hour));
  }

  var newHour = hour === 12 ? 0 : hour;
  return isPm ? newHour + 12 : newHour;
}
function isToday(date) {
  return areSameDay(date, new Date());
}

export { areRangesEqual, areSameDay, areSameMonth, areSameTime, clone, convert24HourMeridiem, get12HourFrom24Hour, get24HourFrom12Hour, getDateBetween, getDateNextMonth, getDateOnlyWithTime, getDatePreviousMonth, getDateTime, getIsPmFrom24Hour, getTimeInRange, isDateValid, isDayInRange, isDayRangeInRange, isMonthInRange, isTimeEqualOrGreaterThan, isTimeEqualOrSmallerThan, isTimeInRange, isTimeSameOrAfter, isToday };
//# sourceMappingURL=dateUtils.js.map
